'use client';

import React, { forwardRef } from 'react';
import { useTranslations } from 'next-intl';
import { FooterProps, FooterLink } from './Footer.types';
import { footerClasses, linksContainerClasses, linkClasses } from './Footer.style';
import { cn } from '@/lib/styleUtils';
import { Link } from '@/lib/app-router';

const FooterLinkItem = ({
  link,
  tFooter,
}: {
  link: FooterLink;
  tFooter: ReturnType<typeof useTranslations<'Footer'>>;
}) => {
  const label = link.displayLabel ?? tFooter(link.labelKey as Parameters<typeof tFooter>[0]);
  if (link?.onClick) {
    return (
      <button
        type="button"
        className={linkClasses}
        onClick={link.onClick}
        data-cy={link.dataCy ?? 'footer-link'}
      >
        {label}
      </button>
    );
  }
  // External links (absolute URLs) open in a new tab via a plain anchor — the
  // app-router Link applies base-path resolution meant for internal routes and
  // would mangle an absolute URL.
  if (link?.external && link.href) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
        data-cy={link.dataCy ?? 'footer-link'}
      >
        {label}
      </a>
    );
  }
  return (
    <Link href={link?.href || '#'} className={linkClasses} data-cy={link.dataCy ?? 'footer-link'}>
      {label}
    </Link>
  );
};

export const Footer = forwardRef<HTMLElement, FooterProps>(({ links = [], className }, ref) => {
  const tFooter = useTranslations('Footer');

  // No copyright line (removed by design) — links are all the footer shows,
  // so an empty link set renders no footer bar at all.
  if (links.length === 0) return null;

  return (
    <footer ref={ref} className={cn(footerClasses, className)}>
      <nav className={linksContainerClasses}>
        {links.map((link, index) => (
          <FooterLinkItem key={index} link={link} tFooter={tFooter} />
        ))}
      </nav>
    </footer>
  );
});

Footer.displayName = 'Footer';
